import { Button, Card, Checkbox, Flex, Form, Input, InputNumber, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { type Beverage } from '../../../../entities';
import { useNotification } from '../../../../hooks/use-notification';
import { useMutation } from '../../../../jsonapi-provider';

const SUCCESS_ADD_MESSAGE = 'Beverage added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Beverage updated successfully';

interface BeverageFormProps {
  beverage: Beverage;
  onSuccess: () => void;
  onCancel: () => void;
}

const BeverageForm = (props: BeverageFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { beverage, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!beverage.id, [beverage.id]);
  const initialValues = beverage;

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const [mutateBeverage] = useMutation<Beverage>(
    beverage?.id ? ['beverage', beverage.id] : ['beverage']
  );

  const onSubmit = (): void => {
    from(formInstance.validateFields())
      .pipe(
        switchMap((values) =>
          from(
            mutateBeverage({
              id: beverage?.id,
              ...values,
            })
          ).pipe(
            switchMap((res) =>
              res.error ? throwError(() => new Error(res.error?.title)) : of(res.data)
            ),
            tap(() => {
              notification.success({
                description: !isEditMode ? SUCCESS_ADD_MESSAGE : SUCCESS_UPDATE_MESSAGE,
                message: '',
              });
              onSuccess();
              formInstance.resetFields();
            }),
            catchError((error) => {
              const [description, title, ..._err] = error?.message.split('\n');
              notification.error({
                description: description ?? 'Failed to save the beverage',
                message: title ?? '',
              });
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  };
  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="beverage-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? beverage.name : 'New Beverage'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>

          <Form.Item label="Price" name="price" rules={[{ required: true }]}>
            <InputNumber addonBefore="$" min={0} precision={2} />
          </Form.Item>
          <Form.Item name="isExtra" valuePropName="checked">
            <Checkbox>Is Extra Beverage</Checkbox>
          </Form.Item>
          <Form.Item name="isTestable" valuePropName="checked">
            <Checkbox>Is Testable Beverage</Checkbox>
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button block htmlType="submit" type="primary">
                Save
              </Button>
              <Button block onClick={onCancel} disabled={false}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default BeverageForm;
